import clsx from 'clsx'

import { ArrowUpRight, DraftSparkle } from '@/assets/icons'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useIsTruncated } from '@/components/ui/tooltip/useIsTruncated'
import { useIsTouchOnlyDevice } from '@/hooks/useIsTouchOnlyDevice'

/** Shared box sizing so the card and its loading placeholder line up in the carousel. */
const kDraftCardBox =
  'flex h-[60px] w-[244px] sm:w-[300px] min-w-[244px] sm:min-w-[300px] items-center gap-2.5 rounded-[5px] border border-brand-neutral-200 px-3'

export type DraftCardProps = {
  /** Draft title shown on the card. */
  name: string
  /** Click handler — opens the draft (caller defines the action). */
  onClick: () => void
  /** Force the hover label open. Storybook/testing only. */
  forceTooltipOpen?: boolean
}

/**
 * DraftCard
 *
 * A single draft card for the Recent Drafts carousel.
 * When the title is clipped, the full name shows in a hover label.
 */
export function DraftCard({ name, onClick, forceTooltipOpen = false }: DraftCardProps) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>()
  // On touch-only devices the tooltip toggles on tap and swallows it, which would block opening the draft.
  // Skip it and let the title truncate (the standard touch pattern).
  const isTouchOnly = useIsTouchOnlyDevice()

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open draft: ${name}`}
      className={clsx(
        'group bg-white text-left hover-capable:hover:bg-brand-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        kDraftCardBox
      )}
    >
      <span className="flex shrink-0 items-center justify-center rounded bg-brand-neutral-100 p-1.5 hover-capable:group-hover:bg-brand-neutral-200">
        <DraftSparkle className="size-5 text-brand-neutral-600" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
        <Tooltip
          title={!isTouchOnly && (isTruncated || forceTooltipOpen) ? name : ''}
          placement="top-start"
          offsetX={-5}
          offsetY={-10}
          forceOpen={forceTooltipOpen}
          disableInteractive // this is so the tooltip doesn't capture the cursor, so the overlapping hover label doesn't steal the card's :hover bg.
        >
          {/* Always one line, so use line-clamp-1, not truncate. break-all fills the line before clipping. */}
          <span ref={ref} className="line-clamp-1 min-w-0 max-w-full break-all text-xs font-medium leading-4 text-brand-neutral-950">
            {name}
          </span>
        </Tooltip>
        <span className="flex items-center gap-0.5 text-[11px] font-medium leading-none text-brand-neutral-600">
          Open
          <ArrowUpRight className="size-3.5" />
        </span>
      </span>
    </button>
  )
}

/**
 * DraftCardSkeleton
 *
 * Loading placeholder for a DraftCard: same box, with a shimmer icon and two title lines.
 * Rendered in the carousel while a matter's drafts are fetching.
 */
export function DraftCardSkeleton() {
  return (
    <div data-testid="draft-card-skeleton" className={clsx('bg-white', kDraftCardBox)}>
      {/* Matches DraftCard's icon box: a 32px square */}
      <LoadingSkeleton width={32} lineHeight={32} />
      <span className="flex min-w-0 flex-1 flex-col gap-1.5">
        <LoadingSkeleton width="100%" lineHeight="0.9rem" />
        <LoadingSkeleton width="20%" lineHeight="0.7rem" />
      </span>
    </div>
  )
}
